<script setup>
import { ref } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import ProductRail from '@/Components/Public/ProductRail.vue'
import StaticLocationMap from '@/Components/Public/StaticLocationMap.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  tour:      { type: Object, required: true },
  base_path: { type: String, default: '/tours' },
  related:   { type: Array, default: () => [] }
})

const activeIdx = ref(0)
const photos = props.tour.photos?.length ? props.tour.photos : []
</script>

<template>
  <Head>
    <title>{{ tour.title }} · {{ t('public.head_title') }}</title>
    <meta name="description" :content="tour.summary || tour.description">
  </Head>

  <BrandHero kind="tour" :eyebrow="t(`vendor_portal.tour.categories.${tour.category}`)" :title="tour.title">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.tours.back') }}</a>
    </template>
    <p v-if="tour.summary" class="bhero-lede">{{ tour.summary }}</p>
    <span v-if="tour.verified" class="bhero-verified">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>{{ t('public.verified') }}
    </span>
    <p class="bhero-price"><span class="fw-value">{{ tour.price_adult }}</span> <span class="bhero-per">/ {{ Math.round(tour.duration_minutes / 60) }}h</span></p>
    <div v-if="tour.contact_intents && Object.keys(tour.contact_intents).length" class="bhero-ctas">
      <a
        v-for="(url, platform, idx) in tour.contact_intents"
        :key="platform"
        :class="['bhero-cta', idx === 0 ? 'bhero-cta--solid' : 'bhero-cta--ghost']"
        :href="url"
        target="_blank"
        rel="noopener"
      >{{ t('public.write_on', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }) }}</a>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <div class="gallery">
        <div class="gallery__main">
          <img v-if="photos[activeIdx]" :src="photos[activeIdx].full" :alt="tour.title" fetchpriority="high">
          <CatalogPlaceholder v-else kind="tour" :category="tour.category" />
        </div>
        <ul v-if="photos.length > 1" class="thumbs">
          <li v-for="(p, i) in photos" :key="p.thumb">
            <button :class="['thumb', i === activeIdx && 'is-active']" @click="activeIdx = i">
              <img :src="p.thumb" alt="" loading="lazy">
            </button>
          </li>
        </ul>
      </div>

      <div class="detail">
        <div class="detail__content">
          <section v-if="tour.highlights?.length" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.highlights') }}</h2>
            <ul class="flist flist--star">
              <li v-for="(x, i) in tour.highlights" :key="i">{{ x }}</li>
            </ul>
          </section>

          <section v-if="tour.description" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.about') }}</h2>
            <p class="prose">{{ tour.description }}</p>
          </section>

          <div v-if="tour.inclusions?.length || tour.not_included?.length" class="pair">
            <section v-if="tour.inclusions?.length" class="card">
              <h2 class="section-title">{{ t('public.tours.sections.included') }}</h2>
              <ul class="flist flist--yes"><li v-for="(x, i) in tour.inclusions" :key="i">{{ x }}</li></ul>
            </section>
            <section v-if="tour.not_included?.length" class="card">
              <h2 class="section-title">{{ t('public.tours.sections.not_included') }}</h2>
              <ul class="flist flist--no"><li v-for="(x, i) in tour.not_included" :key="i">{{ x }}</li></ul>
            </section>
          </div>

          <section v-if="tour.what_to_bring?.length" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.what_to_bring') }}</h2>
            <ul class="flist"><li v-for="(x, i) in tour.what_to_bring" :key="i">{{ x }}</li></ul>
          </section>

          <section v-if="tour.know_before?.length" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.know_before') }}</h2>
            <ul class="flist flist--info"><li v-for="(x, i) in tour.know_before" :key="i">{{ x }}</li></ul>
          </section>

          <section v-if="tour.meeting_point_address || (tour.meeting_point_lat && tour.meeting_point_lng)" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.meeting_point') }}</h2>
            <StaticLocationMap
              v-if="tour.meeting_point_lat && tour.meeting_point_lng"
              :lat="tour.meeting_point_lat"
              :lng="tour.meeting_point_lng"
              :label="tour.title"
            />
            <p v-if="tour.meeting_point_address" class="meet__addr fw-value">{{ tour.meeting_point_address }}</p>
            <p v-if="tour.meeting_point_description" class="prose">{{ tour.meeting_point_description }}</p>
            <a
              v-if="tour.meeting_point_lat && tour.meeting_point_lng"
              class="meet__dir"
              :href="`https://www.google.com/maps/search/?api=1&query=${tour.meeting_point_lat},${tour.meeting_point_lng}`"
              target="_blank"
              rel="noopener"
            >{{ t('public.tours.get_directions') }} →</a>
          </section>
        </div>

        <aside class="detail__aside">
          <div class="facts">
            <h2 class="section-title">{{ t('public.tours.sections.at_a_glance') }}</h2>
            <dl class="dl">
              <div v-if="tour.cancellation_policy === 'free'" class="dl__cancel">
                <dt>{{ t('public.tours.fields.cancellation') }}</dt>
                <dd>✓ {{ t('public.tours.cancellation.free', { hours: tour.cancellation_cutoff_hours }) }}</dd>
              </div>
              <div v-else-if="tour.cancellation_policy === 'non_refundable'">
                <dt>{{ t('public.tours.fields.cancellation') }}</dt>
                <dd>{{ t('public.tours.cancellation.non_refundable') }}</dd>
              </div>
              <div><dt>{{ t('public.tours.fields.duration') }}</dt><dd>{{ Math.round(tour.duration_minutes / 60) }}h</dd></div>
              <div><dt>{{ t('public.tours.fields.price_adult') }}</dt><dd class="dl__price">{{ tour.price_adult }}</dd></div>
              <div v-if="tour.price_child"><dt>{{ t('public.tours.fields.price_child') }}</dt><dd>{{ tour.price_child }}</dd></div>
              <div v-if="tour.max_group_size"><dt>{{ t('public.tours.fields.max_group_size') }}</dt><dd>{{ tour.max_group_size }}</dd></div>
              <div v-if="tour.languages_offered"><dt>{{ t('public.tours.fields.languages_offered') }}</dt><dd>{{ tour.languages_offered }}</dd></div>
            </dl>
          </div>

          <div class="trust-card">
            <h2 class="section-title">{{ t('public.tours.trust.title') }}</h2>
            <ul class="trust-list">
              <li v-if="tour.verified">{{ t('public.tours.trust.verified') }}</li>
              <li>{{ t('public.tours.trust.no_payment') }}</li>
              <li>{{ t('public.tours.trust.real_team') }}</li>
              <li>{{ t('public.tours.trust.local_support') }}</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  </section>

  <section
    v-if="related.length"
    class="related"
  >
    <div class="related__inner">
      <ProductRail
        :title="t('public.tours.related')"
        kind="tour"
        :items="related"
        :item-base="base_path"
        :view-all-href="base_path"
      />
    </div>
  </section>
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 52ch; opacity: 0.94; }
.bhero-verified {
  display: inline-flex; align-items: center; gap: 0.35rem; margin: 1rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 16%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 35%, transparent);
}
.bhero-verified svg { width: 1rem; height: 1rem; }
.bhero-price { margin: 1.1rem 0 0; font-size: clamp(1.6rem, 3vw, 2.1rem); }
.bhero-per { font-size: 1rem; opacity: 0.85; }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }
.bhero-cta {
  display: inline-flex; align-items: center; padding: 0.8rem 1.4rem; border-radius: 999px;
  font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1rem; text-decoration: none;
}
.bhero-cta--solid { background: var(--ink); color: #fff; }
.bhero-cta--ghost { background: color-mix(in oklch, #fff 14%, transparent); color: inherit; border: 1px solid color-mix(in oklch, #fff 45%, transparent); }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 1rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 5rem; height: 4rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.detail { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-top: 1.5rem; }
@media (min-width: 880px) { .detail { grid-template-columns: 1.6fr 0.9fr; gap: 2rem; align-items: start; } }
.detail__content { display: grid; gap: 1rem; }
.pair { display: grid; grid-template-columns: 1fr; gap: 1rem; }
@media (min-width: 560px) { .pair { grid-template-columns: 1fr 1fr; } }
@media (min-width: 880px) { .detail__aside { position: sticky; top: 1.5rem; } }
.facts { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.flist { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.5rem; }
.flist li { position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.5; }
.flist li::before { position: absolute; left: 0; top: 0.05rem; font-weight: 700; }
/* Default marker first; the modifier rules below have equal specificity, so
   they win by later source order. A list with no modifier (What to bring)
   keeps the default dot. Keep this exact order. */
.flist li::before        { content: "•"; color: var(--accent); }
.flist--star li::before  { content: "★"; color: var(--accent); }
.flist--yes li::before   { content: "✓"; color: var(--ok); }
.flist--no li::before    { content: "✗"; color: var(--danger); }
.flist--info li::before  { content: "ℹ"; color: var(--second); }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.prose-inline { white-space: pre-wrap; }
.dl { display: grid; gap: 0.55rem; margin: 0; }
.dl > div { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem; align-items: baseline; }
.dl dt { color: var(--muted); font-size: 0.875rem; }
.dl dd { margin: 0; color: var(--ink); font-size: 1rem; }
.dl__price { color: var(--accent); font-weight: 700; }
.dl__cancel dd { color: var(--ok); font-weight: 600; }
.meet__addr { margin: 0.85rem 0 0.35rem; }
.meet__dir { display: inline-block; margin-top: 0.6rem; font-weight: 700; color: var(--accent); text-decoration: none; }
.meet__dir:hover { text-decoration: underline; }
.related { background: var(--surface); border-top: 1px solid var(--line); }
.related__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem); }
.trust-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.trust-list { list-style: none; margin: 0.75rem 0 0; padding: 0; display: grid; gap: 0.6rem; }
.trust-list li {
  position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.4;
}
.trust-list li::before {
  content: "✓"; position: absolute; left: 0; top: 0;
  color: var(--ok); font-weight: 700;
}
</style>
